import { Centrifuge } from "centrifuge";
import store from "../store";
import { setWsCredentials } from "../store/modules/profile";
import { getRealtimeWsToken } from "../services/realtime";

/**
 * Decode a JWT payload without verifying the signature.
 * Used to read the `channels` claim from the Centrifuge WS token.
 */
const decodeJwtPayload = (token) => {
  try {
    const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64));
  } catch {
    return {};
  }
};

/**
 * Central Centrifuge singleton.
 *
 * Connect once (via useWebSocketManager on login / page load).
 * The service auto-subscribes to the 4 user/company channels embedded in the
 * WS JWT, then broadcasts every publication through onPublication() so any
 * hook or component can react without managing its own subscription.
 *
 * API summary:
 *   centrifugeService.connect({ token, socketUrl })
 *   centrifugeService.disconnect()
 *   centrifugeService.subscribe(channel)          // optional extra channels
 *   centrifugeService.unsubscribe(channel)
 *   centrifugeService.publish(channel, data)      // → Promise
 *   centrifugeService.rpc(method, data)           // → Promise
 *   centrifugeService.onConnectionChange(listener) // → unsubscribe fn
 *   centrifugeService.onPublication(callback)     // → unsubscribe fn
 *   centrifugeService.isSubscribedTo(channel)     // → boolean
 */
class CentrifugeService {
  constructor() {
    this.client = null;
    // Map<channel, Centrifuge.Subscription>
    this.subscriptions = new Map();
    // { onConnecting, onConnected, onDisconnected, onError }[]
    this._connectionListeners = [];
    // (channel: string, data: object) => void[]
    this._publicationListeners = [];
    // Stored so the getToken callback can include it in the Redux update
    this._socketUrl = null;
  }

  // ─── Connection lifecycle ──────────────────────────────────────────────────

  /**
   * Establish a Centrifuge connection.
   *
   * @param {{ token: string, socketUrl: string }} credentials
   *   token     — Centrifuge JWT from login response (data.websocket.token)
   *   socketUrl — WebSocket endpoint (data.websocket.socket_url)
   */
  connect({ token, socketUrl }) {
    if (this.client) this.disconnect();

    this._socketUrl = socketUrl;

    this.client = new Centrifuge(socketUrl, {
      token,
      // Called automatically by Centrifuge when the token is about to expire.
      // Fetches a fresh token from the backend and updates Redux.
      getToken: async () => {
        const res = await getRealtimeWsToken();
        const { token: newToken, socket_url: newSocketUrl } = res.data.data;
        this._socketUrl = newSocketUrl;
        store.dispatch(
          setWsCredentials({ token: newToken, socketUrl: newSocketUrl }),
        );
        return newToken;
      },
      minReconnectDelay: 1000,
      maxReconnectDelay: 30000,
    });

    this.client.on("connecting", (ctx) => {
      this._connectionListeners.forEach((l) => l.onConnecting?.(ctx));
    });

    this.client.on("connected", () => {
      this._connectionListeners.forEach((l) => l.onConnected?.());
      // Only auto-subscribe to user/company channels from the JWT.
      // Store channels are too many (one per store the user can access) and
      // would hit Centrifugo's client_channel_limit. They are subscribed
      // individually via useCentrifugeChannel when the user visits a store page.
      const { channels = [] } = decodeJwtPayload(token);
      channels
        .filter((ch) => !ch.includes("_store_"))
        .forEach((ch) => {
          if (!this.subscriptions.has(ch)) this._createSubscription(ch);
        });
    });

    this.client.on("disconnected", (ctx) => {
      this._connectionListeners.forEach((l) => l.onDisconnected?.(ctx));
    });

    this.client.on("error", (ctx) => {
      console.error("[Centrifuge] Client error:", ctx);
      this._connectionListeners.forEach((l) => l.onError?.(ctx));
    });

    this.client.connect();
  }

  disconnect() {
    this.subscriptions.forEach((sub) => this._destroySub(sub));
    this.subscriptions.clear();

    if (this.client) {
      this.client.disconnect();
      this.client.removeAllListeners();
      this.client = null;
    }

    this._socketUrl = null;
  }

  // ─── Subscription management ───────────────────────────────────────────────

  /**
   * Subscribe to an additional channel (beyond the auto-subscribed ones).
   * Returns the existing subscription if already subscribed.
   */
  subscribe(channel) {
    if (!this.client) {
      console.warn(
        `[Centrifuge] Cannot subscribe to "${channel}" — not connected.`,
      );
      return null;
    }
    if (this.subscriptions.has(channel)) return this.subscriptions.get(channel);
    return this._createSubscription(channel);
  }

  unsubscribe(channel) {
    const sub = this.subscriptions.get(channel);
    if (sub) {
      this._destroySub(sub);
      this.subscriptions.delete(channel);
    }
  }

  isSubscribedTo(channel) {
    return this.subscriptions.has(channel);
  }

  // ─── Messaging ─────────────────────────────────────────────────────────────

  publish(channel, data) {
    if (!this.client)
      return Promise.reject(new Error("[Centrifuge] Not connected"));
    return this.client.publish(channel, data);
  }

  rpc(method, data) {
    if (!this.client)
      return Promise.reject(new Error("[Centrifuge] Not connected"));
    return this.client.rpc(method, data);
  }

  // ─── Event buses ──────────────────────────────────────────────────────────

  /**
   * Register a connection-state listener. Returns an unsubscribe function.
   *
   * @param {{ onConnecting?, onConnected?, onDisconnected?, onError? }} listener
   */
  onConnectionChange(listener) {
    this._connectionListeners.push(listener);
    return () => {
      this._connectionListeners = this._connectionListeners.filter(
        (l) => l !== listener,
      );
    };
  }

  /**
   * Register a publication listener that fires for EVERY channel.
   * Use this in hooks / managers instead of per-channel callbacks.
   * Returns an unsubscribe function.
   *
   * @param {(channel: string, data: object) => void} callback
   */
  onPublication(callback) {
    this._publicationListeners.push(callback);
    return () => {
      this._publicationListeners = this._publicationListeners.filter(
        (l) => l !== callback,
      );
    };
  }

  // ─── Getters ───────────────────────────────────────────────────────────────

  get isConnected() {
    return this.client?.state === "connected";
  }

  get connectionState() {
    return this.client?.state ?? "disconnected";
  }

  // ─── Internal helpers ──────────────────────────────────────────────────────

  _createSubscription(channel) {
    const sub = this.client.newSubscription(channel);

    sub.on("publication", (ctx) => {
      this._publicationListeners.forEach((l) => {
        try {
          l(channel, ctx.data);
        } catch (e) {
          console.error(
            `[Centrifuge] Publication listener error on "${channel}":`,
            e,
          );
        }
      });
    });

    sub.on("error", (ctx) => {
      console.error(`[Centrifuge] Channel "${channel}" error:`, ctx);
    });

    sub.subscribe();
    this.subscriptions.set(channel, sub);
    return sub;
  }

  _destroySub(sub) {
    try {
      sub.unsubscribe();
      sub.removeAllListeners();
    } catch (_) {
      // Subscription may already be removed
    }
  }
}

export const centrifugeService = new CentrifugeService();
